import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import type { PropertiesView, PropertyTable } from '../../core/domain/controller';

/*
 * <vitesse-properties-view> — the ONE renderer for a plugin-authored Properties
 * view, app-wide (controllers now; models/gateways later — the data shape is
 * generic). The plugin provides only DATA + STRUCTURE (titled tables of cells);
 * THIS owns 100% of the styling, identically for every plugin, so a marketplace
 * plugin can't make it look different or ugly.
 *
 * It renders as a ROW VIEW — the same row chrome as the symbol/log grids (34px
 * rows via --vg-row-height, column walls, hover, sticky-feel header), inside the
 * shared `.vg-wrapper` card — so the four controller tabs read as one surface.
 * It is NOT the grid ENGINE (no virtualization/search/resize): Properties is a
 * handful of static facts, so it mirrors the grid's LOOK, not its machinery.
 *
 * A table with no `columns` renders as a key→value sheet (each row = [label,
 * value], two cells); a table WITH `columns` renders with a header row + one
 * cell per column. The platform-enforced bounds are applied HERE so the tab is
 * always clean regardless of who wrote the plugin.
 */
const MAX_TABLES = 8;
const MAX_ROWS = 30;
const MAX_COLS = 6;
const MAX_VALUE_LEN = 200;
const MAX_TITLE_LEN = 40;

const KV_COLUMNS = 'minmax(140px, 280px) 1fr';

const clip = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max - 1)}…` : s;

@customElement('vitesse-properties-view')
export class VitessePropertiesView extends LitElement {
  @property({ attribute: false }) view: PropertiesView = { tables: [] };

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → the shared `.vg-*` + `.props-*` agent-styles apply
  }

  render() {
    const tables = this.view.tables.slice(0, MAX_TABLES);
    if (tables.length === 0) {
      return html`<div class="vg-wrapper vg-properties">
        <div class="vg-empty-state">
          <div class="vg-empty-title">No properties</div>
          <div class="vg-empty-desc">This controller hasn't reported any properties yet.</div>
        </div>
      </div>`;
    }
    return html`<div class="vg-wrapper vg-properties">
      ${tables.map((t) => this.renderTable(t))}
    </div>`;
  }

  private renderTable(t: PropertyTable) {
    const columnar = t.columns.length > 0;
    return html`<section class="props-table">
      <div class="props-title">${clip(t.title, MAX_TITLE_LEN)}</div>
      ${columnar ? this.renderColumnar(t) : this.renderKeyValue(t)}
    </section>`;
  }

  // No `columns` → key→value rows (two cells: dim label, value).
  private renderKeyValue(t: PropertyTable) {
    const rows = t.rows.slice(0, MAX_ROWS);
    const cols = styleMap({ gridTemplateColumns: KV_COLUMNS });
    return html`${rows.map(
      (cells) => html`<div class="props-row" style=${cols}>
        <div class="props-cell props-cell-key">${clip(cells[0] ?? '', MAX_TITLE_LEN)}</div>
        <div class="props-cell props-cell-val" title=${cells[1] ?? ''}>
          ${clip(cells[1] ?? '', MAX_VALUE_LEN)}
        </div>
      </div>`,
    )}`;
  }

  // `columns` set → header row + one cell per column.
  private renderColumnar(t: PropertyTable) {
    const columns = t.columns.slice(0, MAX_COLS);
    const rows = t.rows.slice(0, MAX_ROWS);
    const cols = styleMap({ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` });
    return html`
      <div class="props-head" style=${cols}>
        ${columns.map((c) => html`<div class="props-head-cell">${clip(c, MAX_TITLE_LEN)}</div>`)}
      </div>
      ${rows.map(
        (cells) => html`<div class="props-row" style=${cols}>
          ${columns.map(
            (_, i) => html`<div class="props-cell props-cell-val" title=${cells[i] ?? ''}>
              ${clip(cells[i] ?? '', MAX_VALUE_LEN)}
            </div>`,
          )}
        </div>`,
      )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-properties-view': VitessePropertiesView;
  }
}
